import { useCallback } from "react";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import {
  CollapsibleState,
  isCollapsibleExpanded,
} from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";

/**
 * Toggle a tree node's stored collapsible state. Shared by the chevron button
 * and any keyboard handlers so a row's click and key affordances stay in sync.
 */
export function useCollapseToggle(nodeId: string, collapsibleState: CollapsibleState) {
  const setCollapsibleState = useBuildPageStore((state) => state.setCollapsibleState);
  const isExpanded = isCollapsibleExpanded(collapsibleState);

  const toggle = useCallback(() => {
    setCollapsibleState(
      nodeId,
      isExpanded ? CollapsibleState.Collapsed : CollapsibleState.Expanded,
    );
  }, [setCollapsibleState, nodeId, isExpanded]);

  return { isExpanded, toggle };
}

interface Props {
  /** Tree node id used to track expansion. */
  nodeId: string;
  collapsibleState: CollapsibleState;
  /** Extra classes for the button (e.g. dimming a waiting row). */
  className?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

/**
 * Disclosure chevron for a collapsible tree row. Rows are often links, so the
 * click is stopped from following the row's navigation before toggling.
 */
export function CollapseChevron({
  nodeId,
  collapsibleState,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}: Props) {
  const { isExpanded, toggle } = useCollapseToggle(nodeId, collapsibleState);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggle();
    },
    [toggle],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      tabIndex={-1}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={classNames(
        "group/chevron flex h-6 w-6 cursor-pointer select-none items-center justify-center rounded-sm hover:bg-purple-100",
        className,
      )}
    >
      <Icon
        icon="heroicons/outline/chevron-right"
        className={classNames("h-3 w-3 transition-transform duration-150", {
          "group-hover/chevron:text-charcoal-700 rotate-90 text-gray-600": isExpanded,
        })}
      />
    </button>
  );
}
